import { useCallback, useMemo } from 'react';

import { useAssumedReturn } from '../contexts/AssumedReturnContext';
import {
  findInvestmentsDebtCrossoverYear,
  projectInvestmentBalanceSeries,
  seedInvestmentsVsDebtReturnPct,
  type BalanceSeriesPoint,
} from '../utils/investmentsVsDebt';

type Holding = { value: number; expectedReturnPct?: number };

export type InvestmentsVsDebtModel = {
  /** Current assumed annual return (%). */
  annualRatePct: number;
  /** Portfolio-derived default before user override. */
  seedReturnPct: number;
  investmentPrincipal: number;
  mortgageBalanceSeries: BalanceSeriesPoint[];
  investmentBalanceSeries: BalanceSeriesPoint[];
  crossoverYear: number;
  startYear: number;
  setAnnualRatePct: (value: number) => void;
  nudgeAnnualRatePct: (delta: number) => void;
  resetToPortfolio: () => void;
};

/**
 * Investments vs debt chart model — assumed return is shared with Grow
 * and persisted on the user profile.
 */
export function useInvestmentsVsDebt(params: {
  holdings: Holding[];
  mortgageBalanceSeries: BalanceSeriesPoint[];
  startYear?: number;
}): InvestmentsVsDebtModel {
  const {
    annualRatePct,
    setAnnualRatePct,
    nudgeAnnualRatePct,
  } = useAssumedReturn();

  const seedReturnPct = useMemo(
    () => seedInvestmentsVsDebtReturnPct(params.holdings),
    [params.holdings],
  );
  const investmentPrincipal = useMemo(
    () =>
      params.holdings.reduce((sum, h) => sum + Math.max(0, h.value), 0),
    [params.holdings],
  );
  const startYear =
    params.startYear ??
    params.mortgageBalanceSeries[0]?.year ??
    new Date().getFullYear();

  const resetToPortfolio = useCallback(() => {
    setAnnualRatePct(seedReturnPct);
  }, [seedReturnPct, setAnnualRatePct]);

  const investmentBalanceSeries = useMemo(
    () =>
      projectInvestmentBalanceSeries({
        principal: investmentPrincipal,
        annualRatePct,
        startYear,
      }),
    [investmentPrincipal, annualRatePct, startYear],
  );

  const crossoverYear = useMemo(
    () =>
      findInvestmentsDebtCrossoverYear(
        params.mortgageBalanceSeries,
        investmentBalanceSeries,
        startYear,
      ),
    [params.mortgageBalanceSeries, investmentBalanceSeries, startYear],
  );

  return {
    annualRatePct,
    seedReturnPct,
    investmentPrincipal,
    mortgageBalanceSeries: params.mortgageBalanceSeries,
    investmentBalanceSeries,
    crossoverYear,
    startYear,
    setAnnualRatePct,
    nudgeAnnualRatePct,
    resetToPortfolio,
  };
}
